// File: apps/web/src/components/layout/Navbar.tsx

import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores/auth.store';
import { Button } from '../ui/Button';
import { LanguageSelector } from '../common/LanguageSelector';

export function Navbar() {
  const location = useLocation();
  useTranslation(); // Initialize i18n for language switching
  const { isAuthenticated, user, logout } = useAuthStore();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const navLinkClass = (path: string) => {
    const base = "px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200";
    return isActive(path)
      ? `${base} bg-red-600 text-white shadow-lg shadow-red-600/20`
      : `${base} text-white hover:text-red-500 hover:bg-red-600/10`;
  };

  const mobileNavLinkClass = (path: string) => {
    const base = "block px-4 py-3 rounded-lg text-base font-medium transition-all duration-200";
    return isActive(path)
      ? `${base} bg-red-600 text-white`
      : `${base} text-white hover:bg-red-600/10`;
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-black/70 backdrop-blur-sm">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-12">
        <div className="flex justify-between items-center h-16 lg:h-20">
          {/* Logo and main nav */}
          <div className="flex items-center gap-8">
            <Link to="/" className="flex items-center group">
              <span className="text-2xl lg:text-3xl font-bold text-white tracking-tight">ROAM<span className="text-red-600">.</span></span>
            </Link>

            {/* Main Navigation - Desktop */}
            {isAuthenticated && (
              <div className="hidden lg:flex lg:space-x-2 items-center">
                <Link to="/dashboard" className={navLinkClass('/dashboard')}>
                  Dashboard
                </Link>
                <Link to="/streams" className={navLinkClass('/streams')}>
                  Streams
                </Link>
                <Link to="/teams" className={navLinkClass('/teams')}>
                  Teams
                </Link>
                <Link to="/tournaments" className={navLinkClass('/tournaments')}>
                  Tournaments
                </Link>
                <Link to="/schedule" className={navLinkClass('/schedule')}>
                  Schedule
                </Link>
                <Link to="/discover" className={navLinkClass('/discover')}>
                  Discover
                </Link>
                <Link to="/clips" className={navLinkClass('/clips')}>
                  Clips
                </Link>
                {(user?.role === 'ADMIN') && (
                  <Link to="/admin" className={navLinkClass('/admin')}>
                    Admin
                  </Link>
                )}
                {(user?.role === 'SCOUT' || user?.role === 'ADMIN') && (
                  <Link to="/scout" className={navLinkClass('/scout')}>
                    Scout
                  </Link>
                )}
              </div>
            )}
          </div>

          {/* Right side - User menu */}
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Language selector - compact version */}
            <div className="hidden sm:block">
              <LanguageSelector variant="compact" />
            </div>

            {/* Search button - visible to all */}
            <Link
              to="/search"
              className="p-2 text-white hover:text-red-500 hover:bg-red-600/10 rounded-lg transition-all duration-200"
            >
              <svg className="w-5 h-5 lg:w-6 lg:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </Link>

            {isAuthenticated ? (
              <>
                {/* Desktop actions */}
                <Link
                  to="/notifications"
                  className="relative p-2 text-white hover:text-red-500 hover:bg-red-600/10 rounded-lg transition-all duration-200"
                >
                  <svg className="w-5 h-5 lg:w-6 lg:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </Link>

                <Link
                  to={`/profile/${user?.id}`}
                  className="hidden sm:flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-red-600/10 transition-all duration-200"
                >
                  {user?.avatar ? (
                    <img src={user.avatar} alt={user.displayName || user.firstName} className="w-8 h-8 lg:w-9 lg:h-9 rounded-full border-2 border-red-600/50" />
                  ) : (
                    <div className="w-8 h-8 lg:w-9 lg:h-9 rounded-full bg-gradient-to-br from-red-600 to-red-700 flex items-center justify-center text-white font-bold text-xs lg:text-sm shadow-lg shadow-red-600/20">
                      {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
                    </div>
                  )}
                  <span className="hidden md:block text-sm font-semibold text-white">
                    {user?.displayName || `${user?.firstName} ${user?.lastName}`}
                  </span>
                </Link>

                <Link to="/settings" className="hidden sm:block">
                  <Button variant="ghost" size="icon" className="text-white hover:text-red-500 hover:bg-red-600/10">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </Button>
                </Link>

                <Button onClick={logout} variant="outline" size="sm" className="hidden sm:flex text-white border-white/30 hover:border-red-600 hover:bg-red-600/10">
                  Logout
                </Button>

                {/* Mobile menu button */}
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="lg:hidden p-2 text-white hover:bg-red-600/10 rounded-lg transition-colors"
                >
                  {mobileMenuOpen ? (
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  ) : (
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                  )}
                </button>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" size="sm" className="text-white hover:text-red-500">
                    Login
                  </Button>
                </Link>
                <Link to="/register">
                  <Button size="sm" className="bg-red-600 hover:bg-red-500 text-white shadow-lg shadow-red-600/20">
                    Get Started
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && isAuthenticated && (
        <div className="lg:hidden bg-neutral-900/95 backdrop-blur-md border-t border-neutral-800">
          <div className="px-4 py-4 space-y-2">
            <Link to="/dashboard" className={mobileNavLinkClass('/dashboard')} onClick={closeMobileMenu}>
              Dashboard
            </Link>
            <Link to="/streams" className={mobileNavLinkClass('/streams')} onClick={closeMobileMenu}>
              Streams
            </Link>
            <Link to="/teams" className={mobileNavLinkClass('/teams')} onClick={closeMobileMenu}>
              Teams
            </Link>
            <Link to="/tournaments" className={mobileNavLinkClass('/tournaments')} onClick={closeMobileMenu}>
              Tournaments
            </Link>
            <Link to="/schedule" className={mobileNavLinkClass('/schedule')} onClick={closeMobileMenu}>
              Schedule
            </Link>
            <Link to="/discover" className={mobileNavLinkClass('/discover')} onClick={closeMobileMenu}>
              Discover
            </Link>
            <Link to="/clips" className={mobileNavLinkClass('/clips')} onClick={closeMobileMenu}>
              Clips
            </Link>
            {(user?.role === 'ADMIN') && (
              <Link to="/admin" className={mobileNavLinkClass('/admin')} onClick={closeMobileMenu}>
                Admin
              </Link>
            )}
            {(user?.role === 'SCOUT' || user?.role === 'ADMIN') && (
              <Link to="/scout" className={mobileNavLinkClass('/scout')} onClick={closeMobileMenu}>
                Scout
              </Link>
            )}

            {/* Divider */}
            <div className="border-t border-neutral-700 my-4"></div>

            {/* Mobile-only profile link */}
            <Link
              to={`/profile/${user?.id}`}
              className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-red-600/10 transition-all"
              onClick={closeMobileMenu}
            >
              {user?.avatar ? (
                <img src={user.avatar} alt={user.displayName || user.firstName} className="w-10 h-10 rounded-full border-2 border-red-600/50" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-600 to-red-700 flex items-center justify-center text-white font-bold text-sm">
                  {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
                </div>
              )}
              <div>
                <p className="text-white font-medium">{user?.displayName || `${user?.firstName} ${user?.lastName}`}</p>
                <p className="text-sm text-gray-400">View Profile</p>
              </div>
            </Link>

            <Link to="/settings" className={mobileNavLinkClass('/settings')} onClick={closeMobileMenu}>
              <div className="flex items-center gap-3">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Settings
              </div>
            </Link>

            <button
              onClick={() => { logout(); closeMobileMenu(); }}
              className="w-full text-left px-4 py-3 rounded-lg text-red-500 hover:bg-red-600/10 transition-all flex items-center gap-3"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Logout
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}
